/**
 * forms_controller.js — show/hide the create-record panel (Stimulus).
 *
 * Ports static/js/dashboard-forms.js to an element-scoped controller mounted on
 * #formsContainer (data-controller="forms"), present on the dashboard, quotes,
 * and clients pages. The panel holds the "create client" and "create quote"
 * forms, already rendered in the page.
 *
 * The trigger buttons live OUTSIDE this element and fire through the existing
 * data-action="call-fn" delegation (global-actions.js), with a few inline
 * onclick / inline-script callers as well. To keep every caller working without
 * rewiring them, connect() installs window.showForm / window.hideAllForms as thin
 * shims that delegate to this instance; disconnect() removes them. Turbo-safe: the
 * controller reconnects on each body swap, so the globals are always current. The
 * disconnect guard (only delete if still ours) covers the connect-before-disconnect
 * ordering during a Turbo swap.
 *
 * TWO PRESENTATIONS OF THE SAME MARKUP
 *  * Pointer devices: an inline panel that slides open in the page.
 *  * Touch (the phone PWA and both native shells): the web bottom sheet every
 *    modal gets, the one the status badge opens. For as long as it is open the
 *    panel is dressed as a Bootstrap modal (SHEET_CLASSES), so Bootstrap owns the
 *    backdrop, focus and scroll lock and sheet_controller.js adds the grab handle,
 *    drag-to-dismiss and sheet.css look. Nothing about the sheet is copied here.
 *
 * Never hand these forms to the native sheet host. It opens a second web view
 * that downloads and boots the whole page, so the form arrives late and with app
 * chrome inside it; the in-page sheet shows markup that is already here.
 *
 * FIRST-SESSION EVENTS
 * Opening a form, and closing it without a successful submit, are reported
 * through _onboarding_beacon.js (the server keeps them only for a business in
 * first run). Every close passes through resetForms(), including the instant
 * dismiss when the user leaves the page with the sheet open, so that is where
 * an abandon is decided.
 */
import { Controller } from "@hotwired/stimulus";
import { reportOnboarding } from "./_onboarding_beacon.js";

const SLIDE_MS = 300;

// The web drawer's own test (sheet_controller.js), so this panel and every
// modal agree on which devices get a sheet.
const TOUCH_QUERY = "(hover: none) or (pointer: coarse)";

// What turns the panel into a Bootstrap modal while it is a sheet, as
// [selector within the panel (null for the panel itself), classes]. Added on
// open and removed on close, so the inline panel never carries modal styling.
// The container is both dialog and content: sheet.css positions the dialog and
// styles the content, and the panel has no separate wrapper for either.
const SHEET_CLASSES = [
    [null, ["modal", "fade"]],
    [".enhanced-form-container", ["modal-dialog", "modal-content"]],
    [".form-header", ["modal-header"]],
    [".form-body", ["modal-body"]],
];

export default class extends Controller {
    connect() {
        this.touch = window.matchMedia(TOUCH_QUERY);
        this._showFn = (formType) => this.show(formType);
        this._hideFn = () => this.hide();
        this._onSheetHidden = () => this.sheetHidden();
        this._onBeforeCache = () => this.dismissSheetNow();
        this._onClick = (event) => this.noteDiscard(event);
        this._onSubmitEnd = (event) => this.noteSubmitted(event);
        window.showForm = this._showFn;
        window.hideAllForms = this._hideFn;
        this.element.addEventListener("hidden.bs.modal", this._onSheetHidden);
        this.element.addEventListener("click", this._onClick);
        this.element.addEventListener("turbo:submit-end", this._onSubmitEnd);
        document.addEventListener("turbo:before-cache", this._onBeforeCache);
    }

    disconnect() {
        if (window.showForm === this._showFn) delete window.showForm;
        if (window.hideAllForms === this._hideFn) delete window.hideAllForms;
        this.element.removeEventListener("hidden.bs.modal", this._onSheetHidden);
        this.element.removeEventListener("click", this._onClick);
        this.element.removeEventListener("turbo:submit-end", this._onSubmitEnd);
        document.removeEventListener("turbo:before-cache", this._onBeforeCache);
    }

    /** A successful submit means the open form was saved, not abandoned. */
    noteSubmitted(event) {
        if (event.detail?.success) this.saved = true;
    }

    /**
     * A tap on a button that closes the panel (the header Close, a form's
     * Cancel) is the user throwing the record away. This runs before
     * global-actions.js routes the same click to hideAllForms, and the close
     * it starts reports `discarded`. Every other close (a swipe or backdrop tap
     * on the sheet, Escape, leaving the page, the close after a save) keeps
     * what was typed, so an accidental dismiss loses nothing.
     */
    noteDiscard(event) {
        if (event.target.closest('[data-fn="hideAllForms"]')) this.discarding = true;
    }

    show(formType) {
        const asSheet = this.touch.matches;
        this.panels().forEach((panel) => { panel.style.display = 'none'; });

        const panel = this.element.querySelector(`#${formType}FormContainer`);
        if (panel) {
            this.reveal(panel, !asSheet);
            this.setTitle(panel.dataset.formTitle, panel.dataset.formIcon);
            this.openForm = formType;
            this.saved = false;
            reportOnboarding("create_form_opened", formType);
        }

        if (asSheet) {
            this.openSheet();
            return;
        }

        const formsContainer = this.element;
        if (formsContainer.style.display === 'none') {
            this.reveal(formsContainer, true);
        }
        formsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    hide() {
        const formsContainer = this.element;
        if (!formsContainer) return;

        if (this.isSheet()) {
            // Bootstrap animates the sheet out; sheetHidden resets the panel.
            window.bootstrap.Modal.getOrCreateInstance(formsContainer).hide();
            return;
        }

        formsContainer.classList.add('form-slide-out');
        setTimeout(() => {
            formsContainer.style.display = 'none';
            formsContainer.classList.remove('form-slide-out');
            this.resetForms();
        }, SLIDE_MS);
    }

    // MARK: sheet presentation

    reveal(element, animate) {
        element.style.display = 'block';
        if (!animate) return;
        element.classList.add('form-slide-in');
        setTimeout(() => element.classList.remove('form-slide-in'), SLIDE_MS);
    }

    isSheet() {
        return this.element.classList.contains("modal");
    }

    openSheet() {
        this.setSheetClasses(true);
        // Bootstrap's modal display rules take over from the inline style.
        this.element.style.display = '';
        window.bootstrap.Modal.getOrCreateInstance(this.element, { backdrop: true, keyboard: true }).show();
    }

    /** Fired by Bootstrap once the sheet has animated out, whatever closed it. */
    sheetHidden() {
        this.setSheetClasses(false);
        this.element.style.display = 'none';
        this.resetForms();
    }

    /**
     * Close an open sheet without animation before Turbo snapshots the page, so
     * back never restores a sheet with no Bootstrap instance behind it.
     */
    dismissSheetNow() {
        if (!this.isSheet()) return;
        const instance = window.bootstrap.Modal.getInstance(this.element);
        if (instance) instance.dispose();
        document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        this.element.classList.remove('show');
        this.sheetHidden();
    }

    setSheetClasses(on) {
        for (const [selector, classes] of SHEET_CLASSES) {
            const target = selector ? this.element.querySelector(selector) : this.element;
            if (target) classes.forEach((name) => target.classList.toggle(name, on));
        }
    }

    /**
     * Every create panel in this container.
     *
     * Found by suffix rather than by a list of ids, so a page that adds a
     * form adds markup and nothing here: the panel names itself
     * (`<entity>FormContainer`, matching the `data-entity-id` its button
     * sends) and carries its own header text. The list this replaced had
     * already drifted — it named an `enquiryFormContainer` that no template
     * renders, and opening a third form meant editing this file.
     */
    panels() {
        return this.element.querySelectorAll('[id$="FormContainer"]');
    }

    /** The shared header, on pages that have one. A page with a single form
     *  writes its own heading and passes no data-form-title, so this is a
     *  no-op there rather than two places naming the same form. */
    setTitle(title, icon) {
        const formHeaderTitle = document.getElementById('formHeaderTitle');
        if (!formHeaderTitle || !title) return;
        const element = document.createElement('i');
        element.className = `bi ${icon || 'bi-file-earmark-plus'} me-2`;
        formHeaderTitle.replaceChildren(element, document.createTextNode(title));
    }

    /**
     * Announces the close as `formHidden`. A cancelled close first resets
     * every form in the panel, so the next open shows an empty one; anything
     * a native reset cannot restore (a toggle's shown/hidden block, line
     * items) is cleared by its owner on the form's `reset` event or on
     * `detail.discarded`.
     */
    resetForms() {
        if (this.openForm && !this.saved) reportOnboarding("create_form_abandoned", this.openForm);
        this.openForm = null;
        const discarded = this.discarding === true;
        this.discarding = false;
        if (discarded) this.element.querySelectorAll('form').forEach((form) => form.reset());
        this.panels().forEach((panel) => { panel.style.display = 'none'; });
        this.setTitle('Create New Record', 'bi-file-earmark-plus');
        document.dispatchEvent(new CustomEvent('formHidden', { detail: { discarded } }));
    }
}
